import { Capacitor } from '@capacitor/core';
import {
  getSettings,
  upsertSummaryDocCache,
  upsertSummaryDocCacheBatch,
  removeSummaryDocFromCache,
} from './storage';
import { postCloudAiStream, postCloudAi, putCloudJson } from './cloudApi';
import { anonymizeForAi, collectClientAnonymizeHints } from './anonymizeForAi';
import { getCurrentFirebaseUser } from './firebase';
import { addAiTokenUsageWithSubscriptionNudge } from './aiSubscriptionNudges';

const cleanText = (value, maxWords = null) => {
  let text = String(value || '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/<start_of_turn>|<end_of_turn>|<start_of_image>|<\|im_(start|end)\|>|```|\[END\]|\[INST\]|\[\/INST\]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return '';

  if (maxWords) {
    const words = text.split(' ');
    if (words.length > maxWords) {
      text = words.slice(0, maxWords).join(' ');
      if (!/[.!?]$/.test(text)) text += '.';
    }
  }

  return text;
};

const HISTORY_STREAM_TIMEOUT_MS = 90000;
const HISTORY_STREAM_TIMEOUT_MONTHLY_MS = 150000;
const HISTORY_STREAM_TIMEOUT_YEARLY_MS = 210000;

const toYmdDate = (value) => {
  const s = String(value || '').trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getIsoWeekKey = (dateLike) => {
  const d0 = new Date(`${toYmdDate(dateLike) || toYmdDate(new Date())}T00:00:00`);
  d0.setHours(0, 0, 0, 0);
  d0.setDate(d0.getDate() + 4 - (d0.getDay() || 7));
  const yearStart = new Date(d0.getFullYear(), 0, 1);
  const weekNo = Math.ceil((((d0 - yearStart) / 86400000) + 1) / 7);
  return `${d0.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;
};

const getMonthKey = (dateLike) => {
  const ymd = toYmdDate(dateLike) || toYmdDate(new Date());
  return String(ymd || '').slice(0, 7);
};

const getYearKey = (dateLike) => {
  const ymd = toYmdDate(dateLike) || toYmdDate(new Date());
  return String(ymd || '').slice(0, 4);
};

const getRangeKeyFromEntries = (range, entries) => {
  const list = Array.isArray(entries) ? entries : [];
  const latest = list.length ? list[0]?.date : toYmdDate(new Date());
  if (range === 'yearly') return getYearKey(latest);
  if (range === 'monthly') return getMonthKey(latest);
  return getIsoWeekKey(latest);
};

/** Same key as Firestore `summaries/{range}/items/{key}` (entries newest-first). */
export const getHistorySummaryFirestoreKey = (range, entries) => {
  const safeRange = ['weekly', 'monthly', 'yearly'].includes(String(range || '')) ? String(range) : 'weekly';
  return getRangeKeyFromEntries(safeRange, Array.isArray(entries) ? entries : []);
};

const HEAVY_ENTRY_KEYS = new Set([
  'photos',
  'photoData',
  'photoIds',
  'attachments',
  'embeddedImages',
  'audio',
  'raw',
]);

/** Shrink journal payloads so yearly prompts stay under request limits. */
const slimYearlyRawEntries = (entries) => {
  const list = Array.isArray(entries) ? entries : [];
  return list.slice(0, 110).map(({ date, entry }) => {
    const e = entry && typeof entry === 'object' ? entry : {};
    const next = {};
    for (const [k, v] of Object.entries(e)) {
      if (HEAVY_ENTRY_KEYS.has(k)) continue;
      if (typeof v === 'string') {
        next[k] = v.length > 2500 ? `${v.slice(0, 2500)}…` : v;
      } else if (v && typeof v === 'object' && (k === 'goals' || k === 'goalSnapshots')) {
        next[k] = v;
      } else if (v !== undefined && v !== null && typeof v !== 'object') {
        next[k] = v;
      }
    }
    return { date: String(date || '').trim(), entry: next };
  });
};

const getRangeDateBounds = (entries) => {
  const dates = (Array.isArray(entries) ? entries : [])
    .map((e) => toYmdDate(e?.date))
    .filter(Boolean)
    .sort();
  return {
    startDate: dates[0] || '',
    endDate: dates[dates.length - 1] || '',
  };
};

const summaryDocToEntry = (doc) => ({
  date: String(doc?.endDate || doc?.startDate || doc?.key || ''),
  entry: {
    answer1: String(doc?.text || '').trim(),
  },
});

const fetchSummaryDoc = async (range, key) => {
  try {
    const res = await postCloudAi('/api/summaries/batch-get', { range, keys: [key] });
    const item = Array.isArray(res?.items) && res.items.length ? res.items[0] : null;
    if (item) upsertSummaryDocCache(range, key, item);
    return item;
  } catch {
    return null;
  }
};

const fetchSummaryDocsBatch = async (range, keys) => {
  const list = Array.isArray(keys) ? keys.map((k) => String(k || '').trim()).filter(Boolean) : [];
  if (!list.length) return [];
  try {
    const res = await postCloudAi('/api/summaries/batch-get', { range, keys: list });
    const items = Array.isArray(res?.items) ? res.items : [];
    if (items.length) upsertSummaryDocCacheBatch(range, items);
    return items;
  } catch {
    return [];
  }
};

const saveSummaryDoc = async (range, key, payload) => {
  const data = await putCloudJson(`/api/summaries/${range}/${encodeURIComponent(key)}`, payload);
  if (data?.item) upsertSummaryDocCache(range, key, data.item);
  return data?.item || null;
};

/**
 * Models often return one block or single newlines; normalize so UI can render clear paragraph spacing.
 */
const normalizeHistorySummaryParagraphs = (value) => {
  let t = String(value || '')
    .replace(/\r\n/g, '\n')
    .trim();
  if (!t) return '';

  if (/\n\n/.test(t)) {
    return t
      .split(/\n\n+/)
      .map((b) => b.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join('\n\n');
  }

  const lineParts = t.split(/\n/).map((b) => b.replace(/\s+/g, ' ').trim()).filter(Boolean);
  if (lineParts.length >= 2) {
    return lineParts.join('\n\n');
  }

  const single = lineParts[0] || t.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
  if (single.length < 220 && !/\.(\s|$)/.test(single)) {
    return single;
  }

  const sentences = single.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
  if (sentences.length <= 1) return single;

  const perPara = sentences.length > 12 ? 3 : 2;
  const paras = [];
  for (let i = 0; i < sentences.length; i += perPara) {
    paras.push(sentences.slice(i, i + perPara).join(' '));
  }
  return paras.join('\n\n');
};

const cleanHistorySummaryText = (value, maxWords = null) => {
  let text = String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/<start_of_turn>|<end_of_turn>|<start_of_image>|<\|im_(start|end)\|>|```|\[END\]|\[INST\]|\[\/INST\]/gi, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  if (!text) return '';

  if (maxWords && maxWords > 0) {
    const paras = text.split(/\n\n+/);
    let budget = maxWords;
    const kept = [];
    for (const rawPara of paras) {
      const para = rawPara.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
      if (!para) continue;
      const words = para.split(/\s+/).filter(Boolean);
      if (!words.length) continue;
      if (words.length <= budget) {
        kept.push(words.join(' '));
        budget -= words.length;
      } else {
        let chunk = words.slice(0, budget).join(' ').trim();
        if (budget > 0 && !/[.!?]$/.test(chunk)) chunk += '.';
        if (chunk) kept.push(chunk);
        budget = 0;
      }
      if (budget <= 0) break;
    }
    text = kept.join('\n\n');
  }

  text = normalizeHistorySummaryParagraphs(text);
  return text.trim();
};

const fallbackHistorySummary = (range, entries) => {
  const safeRange = ['weekly', 'monthly', 'yearly'].includes(String(range || '')) ? String(range) : 'weekly';
  const list = Array.isArray(entries) ? entries : [];
  const days = list.map((item) => String(item?.date || '')).filter(Boolean);
  const firstDay = days.length ? days[days.length - 1] : '';
  const lastDay = days.length ? days[0] : '';

  let goalWins = 0;
  let reflections = 0;
  for (const item of list) {
    const entry = item?.entry || item || {};
    const goalStatuses = Object.values(entry?.goals || {});
    goalWins += goalStatuses.filter((status) => status?.completed || status?.easyActivity).length;
    reflections += Object.entries(entry)
      .filter(([key, value]) => /^answer\d+$/.test(key) && value && value !== '–')
      .length;
  }

  const scope = safeRange === 'yearly' ? 'year' : safeRange === 'monthly' ? 'month' : 'week';
  return [
    `Your ${safeRange[0].toUpperCase()}${safeRange.slice(1)} Summary`,
    '',
    `I could not generate the full ${scope} summary this time, but your entries still show momentum from ${firstDay || 'the start of this period'} to ${lastDay || 'today'}.`,
    `You logged ${days.length} day${days.length === 1 ? '' : 's'}, with ${goalWins} goal win${goalWins === 1 ? '' : 's'} and ${reflections} reflection response${reflections === 1 ? '' : 's'}.`,
    `What felt most useful for you in this ${scope}, and what do you want to keep for the next one?`,
  ].join('\n');
};

const toStatementOnly = (value) => {
  const base = cleanText(value || '');
  if (!base) return '';

  const statement = base.replace(/\?/g, '.').replace(/\s+/g, ' ').trim();
  if (!statement) return '';
  return /[.!]$/.test(statement) ? statement : `${statement}.`;
};

const getUserName = () => {
  const settings = getSettings();
  const raw = String(settings?.name || '').trim();
  if (!raw) return 'friend';
  const first = raw
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean)[0] || '';
  return first || 'friend';
};

const isProbablyAiGarbage = (text) => {
  const value = String(text || '').trim();
  if (!value) return true;
  const lower = value.toLowerCase();
  if (lower.includes('subject:') || lower.includes('rebuild-me support')) return true;
  if (lower.includes("here's the user's input") || lower.includes('the user input')) return true;
  if (lower.includes('the user is') || lower.includes('your role is')) return true;
  if (lower.includes('example:') || lower.includes('assistant:') || lower.includes('user:')) return true;
  if (lower.includes('world is a canvas') || lower.includes('paint a brighter hue')) return true;
  if (/<start_of_turn>|<end_of_turn>|<start_of_image>|```/i.test(value)) return true;
  if (/\b20\d{2}-\d{2}-\d{2}\b/.test(value)) return true;
  return false;
};

export const isInvalidAiWelcome = (value) => {
  const text = String(value || '').trim();
  if (!text) return true;
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length < 8) return true;
  if (/^(hi|hello|hey|yo|sup)[.!?,\s]*$/i.test(text)) return true;
  if (isProbablyAiGarbage(text)) return true;
  if (text.length > 220) return true;
  return false;
};

export const isNativeiOS = () => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  } catch {
    return false;
  }
};

export const isAppleIntelligenceSupported = async () => {
  return isNativeiOS();
};

const isBadCompanionReply = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return true;
  if (text.startsWith('subject:') || text.startsWith('re:') || text.startsWith('fw:')) return true;
  if (text.includes('rebuild-me support')) return true;
  if (text.includes('<start_of_turn>') || text.includes('<end_of_turn>') || text.includes('<start_of_image>')) return true;
  if (/\bthe user is\b/.test(text)) return true;
  if (/\byour role is\b/.test(text)) return true;
  if (/\bhere'?s the current state\b/.test(text)) return true;
  if (/\bproject manager\b|\bsoftware development company\b|\blocal community\b/.test(text)) return true;
  if (/\bworld is a canvas\b|\bpaint a brighter hue\b/.test(text)) return true;
  return false;
};

const summarizeEntrySignals = (entries) => {
  const recentEntries = (entries || []).slice(0, 7);
  if (!recentEntries.length) return 'No recent journal entries yet.';

  let hasGoalWins = false;
  let hasReflections = false;

  for (const item of recentEntries) {
    const entry = item?.entry || item;
    const goals = Object.values(entry?.goals || {});
    if (goals.some((status) => status?.completed || status?.easyActivity)) hasGoalWins = true;

    const reflectionCount = Object.entries(entry || {})
      .filter(([key, value]) => /^answer\d+$/.test(key) && value && value !== '–')
      .length;
    if (reflectionCount > 0) hasReflections = true;
  }

  const lines = ['The recent entries show steady showing up.'];
  if (hasGoalWins) lines.push('There is evidence of follow-through on at least one goal.');
  if (hasReflections) lines.push('You are still reflecting, which matters.');
  lines.push('Keep taking the next kind step.');
  return lines.join(' ');
};

export const summarizeHomeChatForJournalDay = async ({ dateStr, messages, signal }) => {
  const transcript = (messages || [])
    .map((m) => `${String(m.role || '').toUpperCase()}: ${String(m.text || '').trim()}`)
    .filter((line) => line.length > 8)
    .join('\n')
    .trim();
  if (!transcript) return { success: false, skipped: true };

  try {
    let full = '';
    await postCloudAiStream(
      '/api/ai/prompt-stream',
      {
        kind: 'home-chat-summary',
        user_name: getUserName(),
        date: dateStr,
        transcript: transcript.slice(0, 12000),
      },
      {
        signal,
        onDelta: (d) => {
          full += d;
        },
        onDone: () => {},
        onError: (err) => {
          throw err;
        },
      },
    );
    const summary = cleanText(String(full || '')).trim();
    if (!summary) return { success: false, error: 'Empty summary' };
    return { success: true, summary };
  } catch (error) {
    console.error('[AI] home chat summary failed', error);
    return { success: false, error: error?.message || 'Summary failed' };
  }
};

/** Streams welcome text via Vertex; onDelta receives cumulative full text after each chunk. */
export const generateWelcomeMessageStream = async (goals, { progressStats = {}, onDelta, onUsage, signal } = {}) => {
  try {
    const now = new Date();
    const hour = now.getHours();
    const localTimeOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';

    let full = '';
    await postCloudAiStream(
      '/api/ai/prompt-stream',
      {
        kind: 'welcome',
        temperature: 0.5,
        user_name: getUserName(),
        goals: (goals || []).slice(0, 4).map((goal) => ({ text: goal.text })),
        stats: {
          checkInCount: Number(progressStats?.checkInCount || 0),
          goalsAchievedCount: Number(progressStats?.goalsAchievedCount || 0),
        },
        context: {
          localHour: hour,
          localTimeOfDay,
        },
      },
      {
        signal,
        onDelta: (d) => {
          full += d;
          onDelta?.(full);
        },
        onDone: (payload) => {
          onUsage?.(payload?.usageEstimate);
        },
        onError: (err) => {
          throw err;
        },
      },
    );

    const response = cleanText(String(full || ''), 40).replace(/\?+/g, '.').replace(/\s+/g, ' ').trim();
    if (isInvalidAiWelcome(response) || isBadCompanionReply(response)) {
      if (response && !isProbablyAiGarbage(response)) {
        return { success: true, response };
      }
      return { success: false, error: 'Invalid welcome response' };
    }

    return { success: true, response };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.error('[AI][welcome prompt-stream] request failed', msg);
    return { success: false, error: msg || 'Failed to generate welcome' };
  }
};

export const generateAIResponse = async (formData, goals, { onStreamDelta, signal } = {}) => {
  try {
    let full = '';
    await postCloudAiStream(
      '/api/ai/prompt-stream',
      {
        kind: 'daily-response',
        user_name: getUserName(),
        form_data: formData,
        goals: goals || [],
      },
      {
        signal,
        onDelta: (d) => {
          full += d;
          onStreamDelta?.(full);
        },
        onDone: () => {},
        onError: (err) => {
          throw err;
        },
      },
    );
    const response = toStatementOnly(cleanText(String(full || ''), 30));
    if (!response || isBadCompanionReply(response)) {
      return { success: false, error: 'No response' };
    }

    return { success: true, response };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getDailyCompletionMessage = async ({ formData, goals, onStreamDelta, signal } = {}) => {
  try {
    const result = await generateAIResponse(formData, goals, { onStreamDelta, signal });
    if (result.success && result.response) {
      return { message: result.response, usedAI: true };
    }
    return {
      message: '',
      usedAI: false,
      error: result?.error || 'AI unavailable',
    };
  } catch {
    return { message: '', usedAI: false, error: 'AI unavailable' };
  }
};

const historySummaryRequestBody = (safeRange, sourceEntries) => ({
  kind: `${safeRange}-summary`,
  user_name: getUserName(),
  entries: sourceEntries || [],
});

const streamHistorySummary = async (safeRange, sourceEntries, { onStreamDelta, signal } = {}) => {
  const body = historySummaryRequestBody(safeRange, sourceEntries);
  const timeoutMs =
    safeRange === 'yearly'
      ? HISTORY_STREAM_TIMEOUT_YEARLY_MS
      : safeRange === 'monthly'
        ? HISTORY_STREAM_TIMEOUT_MONTHLY_MS
        : HISTORY_STREAM_TIMEOUT_MS;

  let full = '';
  let usageFromServer = 0;
  const maxWords = safeRange === 'weekly' ? 220 : safeRange === 'monthly' ? 380 : 800;

  const runStream = () =>
    postCloudAiStream('/api/ai/prompt-stream', body, {
      signal,
      timeoutMs,
      onDelta: (d) => {
        full += d;
        onStreamDelta?.(cleanHistorySummaryText(full, maxWords));
      },
      onDone: (payload) => {
        usageFromServer = Math.max(usageFromServer, Number(payload?.usageEstimate) || 0);
      },
      onError: (err) => {
        throw err;
      },
    });

  try {
    await runStream();
  } catch (firstErr) {
    try {
      const data = await postCloudAi('/api/ai/prompt-once', body);
      full = String(data?.text ?? data?.response ?? '');
      usageFromServer = Math.max(usageFromServer, Number(data?.usageEstimate) || 0);
    } catch {
      throw firstErr;
    }
  }

  let out = cleanHistorySummaryText(String(full || ''), maxWords);
  if (!out) {
    try {
      const data = await postCloudAi('/api/ai/prompt-once', body);
      full = String(data?.text ?? data?.response ?? '');
      usageFromServer = Math.max(usageFromServer, Number(data?.usageEstimate) || 0);
      out = cleanHistorySummaryText(String(full || ''), maxWords);
    } catch {
      /* leave empty */
    }
  }

  const uid = getCurrentFirebaseUser()?.uid;
  if (uid && out) {
    const est = usageFromServer || Math.ceil(String(out).length / 3.5);
    if (est > 0) {
      addAiTokenUsageWithSubscriptionNudge(uid, est);
    }
  }

  return out;
};

const ensureRangeKeyDoc = async (safeRange, key, rows) => {
  const existing = await fetchSummaryDoc(safeRange, key);
  if (existing?.text) return existing;
  const generated = await streamHistorySummary(safeRange, rows, {});
  if (!generated || isBadCompanionReply(generated)) return null;
  const bounds = getRangeDateBounds(rows);
  return saveSummaryDoc(safeRange, key, {
    text: generated,
    ...bounds,
    sourceType: 'raw_entries',
    sourceKeys: [],
    generatedAt: new Date().toISOString(),
  });
};

const deriveWeekKeysFromEntries = (entries) => Array.from(
  new Set((Array.isArray(entries) ? entries : []).map((e) => getIsoWeekKey(e?.date)).filter(Boolean)),
).sort();

const deriveMonthKeysFromEntries = (entries) => Array.from(
  new Set((Array.isArray(entries) ? entries : []).map((e) => getMonthKey(e?.date)).filter(Boolean)),
).sort();

const buildMonthlySourceEntries = async (entries) => {
  const weekKeys = deriveWeekKeysFromEntries(entries);
  const existingWeekly = await fetchSummaryDocsBatch('weekly', weekKeys);
  const haveMap = new Map(existingWeekly.map((x) => [String(x.key || ''), x]));
  const missing = weekKeys.filter((k) => !haveMap.has(k));
  for (const wk of missing) {
    const rows = (Array.isArray(entries) ? entries : []).filter((e) => getIsoWeekKey(e?.date) === wk);
    if (!rows.length) continue;
    const made = await ensureRangeKeyDoc('weekly', wk, rows);
    if (made) haveMap.set(wk, made);
  }
  const docs = weekKeys.map((k) => haveMap.get(k)).filter((d) => d && d.text);
  return docs.length ? docs.map(summaryDocToEntry) : (Array.isArray(entries) ? entries : []);
};

const buildYearlySourceEntries = async (entries) => {
  const monthKeys = deriveMonthKeysFromEntries(entries);
  const existingMonthly = await fetchSummaryDocsBatch('monthly', monthKeys);
  const haveMap = new Map(existingMonthly.map((x) => [String(x.key || ''), x]));
  const missing = monthKeys.filter((k) => !haveMap.has(k));
  for (const mk of missing) {
    const rows = (Array.isArray(entries) ? entries : []).filter((e) => getMonthKey(e?.date) === mk);
    if (!rows.length) continue;
    const monthlySource = await buildMonthlySourceEntries(rows);
    const generated = await streamHistorySummary('monthly', monthlySource, {});
    if (!generated || isBadCompanionReply(generated)) continue;
    const bounds = getRangeDateBounds(rows);
    const made = await saveSummaryDoc('monthly', mk, {
      text: generated,
      ...bounds,
      sourceType: 'weekly_summaries',
      sourceKeys: deriveWeekKeysFromEntries(rows),
      generatedAt: new Date().toISOString(),
    });
    if (made) haveMap.set(mk, made);
  }
  const docs = monthKeys.map((k) => haveMap.get(k)).filter((d) => d && d.text);
  if (docs.length) return docs.map(summaryDocToEntry);
  const list = Array.isArray(entries) ? entries : [];
  return slimYearlyRawEntries(list);
};

export const generateHistorySummary = async (
  range,
  entries,
  { onStreamDelta, signal, forceRegenerate = false } = {},
) => {
  const safeRange = ['weekly', 'monthly', 'yearly'].includes(String(range || '')) ? String(range) : 'weekly';
  const sourceEntries = Array.isArray(entries) ? entries : [];
  const key = getRangeKeyFromEntries(safeRange, sourceEntries);
  const summaryMaxWords = safeRange === 'weekly' ? 220 : safeRange === 'monthly' ? 380 : 800;

  if (forceRegenerate) {
    removeSummaryDocFromCache(safeRange, key);
  }

  if (!forceRegenerate) {
    const cached = await fetchSummaryDoc(safeRange, key);
    if (cached?.text) {
      return {
        success: true,
        response: cleanHistorySummaryText(cached.text, summaryMaxWords),
        cached: true,
        generatedAt: String(cached.generatedAt || '').trim(),
      };
    }
  }

  try {
    let summarySource = sourceEntries;
    let sourceType = 'raw_entries';
    let sourceKeys = [];
    if (safeRange === 'monthly') {
      summarySource = await buildMonthlySourceEntries(sourceEntries);
      sourceType = 'weekly_summaries';
      sourceKeys = deriveWeekKeysFromEntries(sourceEntries);
    } else if (safeRange === 'yearly') {
      summarySource = await buildYearlySourceEntries(sourceEntries);
      sourceType = 'monthly_summaries';
      sourceKeys = deriveMonthKeysFromEntries(sourceEntries);
    }

    let response = '';
    try {
      response = await streamHistorySummary(safeRange, summarySource, { onStreamDelta, signal });
    } catch (firstError) {
      if (safeRange !== 'weekly') {
        const retryBody = safeRange === 'yearly'
          ? slimYearlyRawEntries(sourceEntries)
          : sourceEntries;
        const body = retryBody.length ? retryBody : sourceEntries;
        response = await streamHistorySummary(safeRange, body, { onStreamDelta, signal });
        sourceType = 'raw_entries';
        sourceKeys = [];
      } else {
        throw firstError;
      }
    }
    if ((!response || isBadCompanionReply(response)) && safeRange === 'yearly') {
      const slim = slimYearlyRawEntries(sourceEntries);
      if (slim.length) {
        response = await streamHistorySummary('yearly', slim, { onStreamDelta, signal });
        sourceType = 'raw_entries';
        sourceKeys = [];
      }
    }
    if (!response || isBadCompanionReply(response)) {
      const fallback = fallbackHistorySummary(safeRange, sourceEntries);
      return { success: true, response: fallback, fallback: true };
    }

    const bounds = getRangeDateBounds(sourceEntries);
    const saved = await saveSummaryDoc(safeRange, key, {
      text: response,
      ...bounds,
      sourceType,
      sourceKeys,
      generatedAt: new Date().toISOString(),
    });
    const generatedAt = String(saved?.generatedAt || '').trim() || new Date().toISOString();
    return { success: true, response, generatedAt };
  } catch (error) {
    const fallback = fallbackHistorySummary(safeRange, sourceEntries);
    return { success: true, response: fallback, fallback: true, error: error?.message || 'summary-timeout' };
  }
};

export const seedSummaryHierarchyFromEntries = async (entries) => {
  const list = Array.isArray(entries) ? entries : [];
  if (!list.length) return { ok: true, generated: 0 };
  let generated = 0;
  try {
    const weekKeys = deriveWeekKeysFromEntries(list).slice(-52);
    for (const wk of weekKeys) {
      const rows = list.filter((e) => getIsoWeekKey(e?.date) === wk);
      const before = await fetchSummaryDoc('weekly', wk);
      if (!before?.text) {
        const made = await ensureRangeKeyDoc('weekly', wk, rows);
        if (made?.text) generated += 1;
      }
    }
    const monthKeys = deriveMonthKeysFromEntries(list).slice(-24);
    for (const mk of monthKeys) {
      const rows = list.filter((e) => getMonthKey(e?.date) === mk);
      const before = await fetchSummaryDoc('monthly', mk);
      if (!before?.text) {
        const monthlySource = await buildMonthlySourceEntries(rows);
        const text = await streamHistorySummary('monthly', monthlySource, {});
        if (text && !isBadCompanionReply(text)) {
          await saveSummaryDoc('monthly', mk, {
            text,
            ...getRangeDateBounds(rows),
            sourceType: 'weekly_summaries',
            sourceKeys: deriveWeekKeysFromEntries(rows),
            generatedAt: new Date().toISOString(),
          });
          generated += 1;
        }
      }
    }
    return { ok: true, generated };
  } catch (e) {
    return { ok: false, generated, error: String(e?.message || e || 'seed-failed') };
  }
};

export const generateWeeklySummary = async (entries, opts = {}) => generateHistorySummary('weekly', entries, opts);

/** Normalize onboarding goals to the conversational model (no AI expansion). */
export const generateDailyTasksFromGoals = async (goals) => {
  const sourceGoals = Array.isArray(goals) ? goals.filter((g) => String(g?.text || '').trim()) : [];
  const now = new Date().toISOString();
  return sourceGoals.slice(0, 5).map((goal, index) => ({
    id: String(goal.id || '').trim() || `goal_${index + 1}`,
    text: String(goal.text || '').trim(),
    enabled: goal.enabled !== false,
    createdAt: typeof goal.createdAt === 'string' && goal.createdAt ? goal.createdAt : now,
    lastMentionedAt: typeof goal.lastMentionedAt === 'string' ? goal.lastMentionedAt : '',
  }));
};

export default {
  isNativeiOS,
  isAppleIntelligenceSupported,
  generateAIResponse,
  getDailyCompletionMessage,
  generateHistorySummary,
  generateWeeklySummary,
  seedSummaryHierarchyFromEntries,
  generateDailyTasksFromGoals,
  generateWelcomeMessageStream,
  summarizeHomeChatForJournalDay,
};
